<template>
  <v-container 
    v-if="!$apollo.loading"
    fill-height
    fluid
    grid-list-xl
  >
    <v-layout
      v-if="!editMode"
      justify-center
      wrap
    >
      <v-flex
        md6
        xs12
        mb-0
        mt-0
        pb-0
      >
        <h2>{{ $t('modules.courses') }}</h2>
      </v-flex>
      <v-flex
        md6
        xs12
        mb-0
        :class="!$vuetify.breakpoint.xs ? 'pt-11 text-right': 'pb-5'"
        pb-0
      >
        <v-btn
          style="display:inline;margin-top:-6px;"
          small
          class="primary"
          @click="editMode = true"
        >
          {{ $t('globals.createButton') }}
        </v-btn>
      </v-flex>
      <v-flex
        v-if="moduleLmsCourses && moduleLmsCourses.data.length > 0"
        md12
      >
        <v-card>
          <v-data-table
            class="text-no-wrap" 
            :headers="headers"
            :items="moduleLmsCourses.data"
            :hide-default-footer="true"
            :mobile-breakpoint="900"
            :items-per-page="200"
          >
            <template
              slot="headerCell"
              slot-scope="{ header }"
            >
              <span
                v-text="header.text"
              />
            </template>
            <template v-slot:item.image="{ item }">
              <v-avatar
                :tile="true"
                :size="40"
              >
                <v-img
                  :src="(item.image || require('@/images/no-image.png'))"
                  :lazy-src="item.image"
                  :max-height="item.image ? '60' : '24'"
                  :style="(item.image && item.bg_color) && 'border: 1px solid ' + item.bg_color"
                  contain
                />
              </v-avatar>  
            </template>
            <template v-slot:item.name="{ item }">
              {{ item.name }}
            </template>
            <template v-slot:item.lectures_number="{ item }">
              {{ item.lectures_number || 0 }}
            </template>
            <template v-slot:item.status="{ item }">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-icon
                    small
                    :color="item.status == 'active' ? 'success': 'error'"
                    v-on="on"
                  >
                    {{ item.status == 'active' ? 'mdi-check': 'mdi-close' }}
                  </v-icon>
                </template>
                {{ $t(item.status == 'active' ? 'modules.courseStatusActive' : 'modules.courseStatusInactive') }}
              </v-tooltip>
            </template>
            <template v-slot:item.created_at="{ item }">
              <small>{{ item.created_at | moment("L") }}</small>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-menu
                offset-y
                left
                bottom
              >
                <template v-slot:activator="{ on }">
                  <v-btn
                    outlined 
                    small
                    text
                    v-on="on"
                  >
                    {{ $t('globals.manageButton') }}
                  </v-btn>
                </template>
                <v-sheet dense>
                  <v-list-item
                    @click="editCourse(item)"
                  >
                    <v-list-item-title>{{ $t('globals.editButton') }}</v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    @click="confirmDeleteCourse(item)"
                  >
                    <v-list-item-title>{{ $t('globals.deleteButton') }}</v-list-item-title>
                  </v-list-item>
                </v-sheet>
              </v-menu>
            </template>
          </v-data-table>
        </v-card>
      </v-flex>
      <v-flex
        v-else
        class="not-found"
      >
        <v-card>
          <v-icon>mdi-ghost</v-icon>
          <p>{{ $t('modules.coursesNotFound') }}</p>
        </v-card>
      </v-flex>
    </v-layout>
    <edit-form
      v-if="editMode"
      :course="selectedCourse"
      :whitelabel-uuid="getWhiteLabel.uuid"
      @closeEditMode="closeEditMode"
    />
    <helper-confirm-dialog
      v-if="confirmDialog"
      :show-dialog="confirmDialog"
      :text="$t('modules.courseReallyDelete', {name: selectedCourse.name})"
      @closeConfirmDialog="closeConfirmDialog"
    />
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import LMS_COURSES_QUERY from "@/graphql/queries/modules/LmsCourses.gql";
import COURSE_DELETE_MUTATION from "@/graphql/mutations/modules/CourseDelete.gql";
import EditForm from "@/views/Modules/Lms/NewOrEditForm.vue";

export default {
  name: 'ModuleLms',
  components: {
    EditForm
  },
  data() {
    return {
      params: {
        whitelabel_uuid: null,
        count: 20,
        page: 1
      },
      loading: false,
      editMode: false,
      selectedCourse: null,
      confirmDialog: false,
      search: "",
      headers: [
        {
          sortable: false,
          text: '',
          value: 'image'
        },
        {
          sortable: false,
          text: this.$t('modules.courseName'),
          value: 'name',
        },
        {
          sortable: false,
          text: this.$t('modules.courseLessons'),
          value: 'lectures_number'
        },
        {
          sortable: false,
          text: this.$t('modules.courseStatus'),
          value: 'status'
        },
        {
          sortable: false,
          text: this.$t('modules.courseAddedAt'),
          value: 'created_at'
        },
        {
          sortable: false,
          text: '',
          value: 'actions',
        },
      ]
    }
  },
  computed: {
    ...mapGetters("whitelabel", ["getWhiteLabel"]),
    moduleData() {
      if (this.getWhiteLabel && this.getWhiteLabel.modules.length > 0) {
        return this.getWhiteLabel.modules.find(item => item.module == 'lms')
      }
      return null
    }
  },
  watch: {
    getWhiteLabel(val) {
      if (this.routeNotAllowed(val, 'lms')) {
        this.$router.push('/dashboard')
      }
      this.params.whitelabel_uuid = val.uuid
      this.moduleLmsCourses = []
      this.$apollo.queries.moduleLmsCourses.refetch()
    }
  },
  mounted() {
    if (this.getWhiteLabel) {
      this.params.whitelabel_uuid = this.getWhiteLabel.uuid
    }
  },
  methods: {
    closeConfirmDialog(confirmed) {
      this.confirmDialog = false
      if (confirmed) {
        this.deleteCourse()
      }
      this.selectedProduct = null
    },
    confirmDeleteCourse(item) {
      this.confirmDialog = true
      this.selectedCourse = item
    },
    editCourse(item) {
      this.selectedCourse = item
      this.editMode = true
    },
    deleteCourse() {
      this.loading = true; 
      let params = {
        whitelabel_uuid: this.getWhiteLabel.uuid,
        course_uuid: this.selectedCourse.uuid
      }
      this.$apollo
        .mutate({
          mutation: COURSE_DELETE_MUTATION,
          variables: params
        })
        .then(data => {
          this.$store.commit('app/setNotifier', this.$t('globals.success'));
          this.$apollo.queries.moduleLmsCourses.refetch();
          this.selectedCourse = null
        })
        .catch(error => {
          this.errorHandler(error);
          this.loading = false;
          this.selectedCourse = null;
        });
    },
    closeEditMode(val) {
      this.editMode = false
      if(val) this.$apollo.queries.moduleLmsCourses.refetch();
      this.selectedCourse = null
    },
    reload() {
      this.$apollo.queries.moduleLmsCourses.fetchMore({
        variables: this.params,
        updateQuery: (previousResult, { fetchMoreResult }) => {
          return fetchMoreResult
        },
      })
    },
  },
  apollo: {
    moduleLmsCourses: {
      fetchPolicy: "network-only",
      query() {
        return LMS_COURSES_QUERY;
      },
      variables() {
        return this.params
      },
      skip() {
        return !this.params.whitelabel_uuid;
      },
    }
  },
}
</script>
<style lang="scss" scoped>
 
</style>
